import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, CircularProgress, Box, Typography, Avatar } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { handleSearch as fetchBookSuggestions } from '../../Utils/search.ts';

export default function SearchBar({ onSearch, isMobile = false }) {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleInputChange = async (event, value, reason) => {
        setInputValue(value);
        if (reason === 'input' && value.length > 1) {
            setLoading(true);
            await fetchBookSuggestions(value, setOptions, setLoading);
        } else if (reason === 'clear' || value.length === 0) {
            setOptions([]);
        }
    };

    const handleOptionSelect = (event, value) => {
        if (value && value.title) {
            onSearch(value.title);
        } else if (typeof value === 'string') {
            onSearch(value);
        } else if (inputValue) {
            onSearch(inputValue);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (inputValue) onSearch(inputValue);
        }
    };

    return (
        <Autocomplete
            freeSolo
            options={options}
            loading={loading}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
            filterOptions={(x) => x} // Don't filter client-side
            onInputChange={handleInputChange}
            onChange={handleOptionSelect}
            inputValue={inputValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    onKeyDown={handleKeyDown}
                    placeholder={isMobile ? "Search textbook or ISBN..." : "Search by ISBN, textbook name, author"}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start" sx={{ ml: 1 }}>
                                <MenuIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                <InputAdornment position="end" sx={{ mr: 1 }}>
                                    <IconButton onClick={() => onSearch(inputValue)}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            </>
                        ),
                    }}
                    sx={{
                        width: isMobile ? '100%' : '450px',
                        alignSelf: 'center',
                        borderRadius: '28px',
                        backgroundColor: '#FFFFFF',
                        '& .MuiOutlinedInput-root': {
                            height: isMobile ? '40px' : '55px',
                            '& fieldset': {
                                border: 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                            },
                        },
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {option.thumbnail && (
                        <Avatar src={option.thumbnail} alt={option.title} variant="square" sx={{ width: 40, height: 60, mr: 1 }} />
                    )}
                    <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{option.title}</Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>by {option.authors}</Typography>
                    </Box>
                </Box>
            )}
        />
    );
}